/**
 * Client-side error reporter — the browser half of the telemetry sink.
 *
 * Installs window error/unhandledrejection listeners and POSTs a bounded
 * report to POST /api/v1/telemetry/client-error (anonymous), so a silent JS
 * crash on a tester's screen becomes a queryable App Insights trace on our
 * side INSTEAD of something we only hear about if they bother to tell us.
 *
 * Design constraints (this code runs precisely when things are broken):
 *   - Zero dependencies, tiny (bundle-budget-safe), raw fetch with keepalive
 *     so a report survives an immediate navigation/unload.
 *   - Loop-safe: dedupe identical messages, hard cap per session, and NEVER
 *     report an error that came from the reporter's own fetch (that would be a
 *     self-amplifying loop).
 *   - Fail-silent: any throw inside the reporter is swallowed — telemetry must
 *     never become the thing that breaks the page.
 *   - Install BEFORE React renders so boot-time crashes are captured too.
 */

const API_BASE_URL = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';
const ENDPOINT = `${API_BASE_URL}/api/v1/telemetry/client-error`;
const RELEASE =
  (import.meta.env['VITE_RELEASE'] as string | undefined) ??
  (import.meta.env['MODE'] as string | undefined) ??
  'unknown';

const MAX_PER_SESSION = 25; // bound a runaway error loop
const seen = new Set<string>();
let sent = 0;
let installed = false;

function post(payload: Record<string, unknown>): void {
  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true, // survive an imminent unload/navigation
      // No credentials/cookies — the endpoint is anonymous.
    }).catch(() => undefined); // a failed report must never surface
  } catch {
    /* sendBeacon/fetch unavailable — give up silently */
  }
}

function report(kind: string, message: string, stack?: string): void {
  try {
    if (sent >= MAX_PER_SESSION) return;
    if (!message) return;
    // Never report our own telemetry POST failing — that would self-amplify.
    // A failure of the reporter's fetch surfaces with the endpoint in its
    // message; that's the loop signal to suppress.
    if (message.includes('/v1/telemetry/client-error')) return;
    const key = `${kind}:${message}`.slice(0, 200);
    if (seen.has(key)) return; // dedupe identical errors
    seen.add(key);
    sent += 1;
    post({
      kind,
      message: String(message).slice(0, 1000),
      stack: stack ? String(stack).slice(0, 4000) : undefined,
      url: typeof location !== 'undefined' ? location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      release: RELEASE,
    });
  } catch {
    /* swallow — the reporter must never throw */
  }
}

export function installClientErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    const err = e.error as Error | undefined;
    report('error', err?.message ?? e.message ?? 'Unknown error', err?.stack);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason as unknown;
    const message =
      reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Unhandled promise rejection';
    const stack = reason instanceof Error ? reason.stack : undefined;
    report('unhandledrejection', message, stack);
  });
}

/** Test-only: reset the dedupe/cap counters between specs. Deliberately does
 *  NOT reset `installed` — the window listeners are added once and persist
 *  for the page lifetime; re-installing would stack duplicate listeners. */
export function __resetClientErrorReporterForTests(): void {
  seen.clear();
  sent = 0;
}
